/*
 * hide-upload-property.js  (v4 — multi-property + priority ordering)
 * ------------------------------------------------------------
 * Renames specific line item properties to "_"-prefixed names
 * right before the cart-add request is sent. Shopify auto-hides
 * properties whose names start with "_" from the cart, checkout,
 * and customer emails. Admin order detail still shows them.
 *
 * Currently renames:
 *   • "Upload (Vector Files Preferred)" → "_Upload (Vector Files Preferred)"
 *   • "Preview"                          → "_Preview"
 *   • "Edit"                             → "_Edit"
 *
 * In addition, when the renamed body is rebuilt we move
 * "_Upload (Vector Files Preferred)" to the FIRST position so it
 * shows at the top of the admin order detail (Shopify preserves
 * line item property submission order).
 *
 * Pre-submit JS that reads the fields by their visible names keeps
 * working — the rename + reorder happen only at the request boundary.
 *
 * RULE (Owen 2026-10-09): a Design Note wipes every reorder reference.
 *   A line that carries a non-empty "Design Notes" is NOT an exact repeat,
 *   so "_reorder" / "design re-used" (and their hidden twins) are dropped
 *   from the payload before it leaves the browser. Enforced here because
 *   this is the one boundary every add path crosses — the legacy engines
 *   (bySize, bySize_popular, pdp_transferBySize, ninja-multiupload,
 *   __blanksUploadHelper, nt-bgr-common) and bysize-v4 all stamp the tag
 *   separately, and gating each writer would drift. The cart's own notes
 *   editor applies the same rule to notes added AFTER the add
 *   (component-cart.js, the /cart/change resend).
 *
 * RULE (Justin 2026-10-28): one note only, the print-ready `_reorder`.
 *   ("THIS IS AN EXACT REORDER OF …" / "SIMILAR REORDER OF …", nt-print-ready.js).
 *   Every other reorder note is retired and never leaves the browser, from
 *   any add path; lines already in a cart lose them in the sweep below:
 *   `_reorder` / `reorder` = "Design previously used on #NT-…" or the My Orders
 *   Reorder buttons' "of order #NT-… / <line>", and `design re-used` in any
 *   form ("previously on order # NT-…", or a saved design's library photo id).
 *   (It replaces the 2026-10-13 one-reference rule.) The photo id was also a
 *   fallback for bg-removal-multi / nt-bgr-engine's library resave, which take
 *   it from the picker click first; the "of order #" value is still read, before
 *   it is dropped, as the Design Note rule's carried-note signal. The writers
 *   (bysize-v4, nt-bgr-common, the legacy engines, My Designs' quick add, the
 *   My Orders buttons) still stamp them; this boundary is where they stop, for
 *   the same reason as the Design Note rule.
 */
(function () {
  'use strict';

  /* ---------- Design Note wipes reorder references ---------- */
  var NOTE_KEYS    = ['Design Notes', '_Design Notes'];
  var REORDER_KEYS = ['_reorder', 'reorder', 'design re-used', '_design re-used'];
  function isNoteKey(key)    { return NOTE_KEYS.indexOf(key) !== -1; }
  function isReorderKey(key) { return REORDER_KEYS.indexOf(key) !== -1; }
  /* The My Orders "Reorder" buttons copy the ORIGINAL line's properties —
     its Design Notes included — and stamp `reorder: "of order #NT-… / <line>"`
     (members-portal-layout / all-orders / my-orders-content). That note was
     not typed for this add, it is the original's, so an exact repeat keeps its
     reorder reference. Only those flows produce the "of order #" value; a
     note typed on a product page arrives beside "Design previously used on #"
     / "previously on order #" values and still strips. If the customer then
     CHANGES the carried note in the cart, the cart editor strips (see
     component-cart.js). */
  function isCarriedReorderValue(v) { return /^\s*of order #/i.test(String(v == null ? '' : v)); }
  /* `properties[<key>]` form name -> bare key, else null */
  function bareKey(name) {
    var m = /^properties\[(.*)\]$/.exec(String(name || ''));
    return m ? m[1] : null;
  }
  function hasText(v) { return typeof v === 'string' && v.trim() !== ''; }
  /* [name, value] pairs (form names). Returns the filtered pairs when a
     non-empty note is present and at least one reorder key was removed,
     else null (caller keeps the original). */
  function stripReorderPairs(pairs) {
    var noted = false, carried = false;
    pairs.forEach(function (p) {
      var k = bareKey(p[0]);
      if (k === null) return;
      if (isNoteKey(k) && hasText(p[1])) noted = true;
      if (isReorderKey(k) && isCarriedReorderValue(p[1])) carried = true;
    });
    if (!noted || carried) return null;
    var out = pairs.filter(function (p) {
      var k = bareKey(p[0]);
      return !(k !== null && isReorderKey(k));
    });
    return out.length === pairs.length ? null : out;
  }
  /* bare-key object (JSON payloads), mutated in place; true when stripped */
  function stripReorderInObject(src) {
    var noted = false, carried = false;
    Object.keys(src).forEach(function (k) {
      if (isNoteKey(k) && hasText(src[k])) noted = true;
      if (isReorderKey(k) && isCarriedReorderValue(src[k])) carried = true;
    });
    if (!noted || carried) return false;
    var hit = false;
    Object.keys(src).forEach(function (k) { if (isReorderKey(k)) { delete src[k]; hit = true; } });
    return hit;
  }

  /* ---------- One note only: the print-ready _reorder (see header) ---------- */
  var REUSED_KEYS = ['design re-used', '_design re-used'];
  var PRIMARY_REORDER_KEYS = ['_reorder', 'reorder'];
  var PRINT_READY_NOTE = /^\s*(this is an exact reorder|similar reorder)/i;
  function isRetiredNote(key, v) {
    return REUSED_KEYS.indexOf(key) !== -1 ||
      (PRIMARY_REORDER_KEYS.indexOf(key) !== -1 && !PRINT_READY_NOTE.test(String(v == null ? '' : v)));
  }
  /* Remove Background / Super Resolution only reach the order when they are Yes (Justin 2026-11-02): a "No"
     is noise on every line. Applied to NEW adds only (not the cart sweep, which would rewrite lines already in
     carts). A missing flag already reads as off everywhere (cart-edit-size.js flag() treats "no" = missing). */
  var OFF_FLAG_KEYS = ['Remove Background', '_Remove Background', 'Super Resolution', '_Super Resolution'];
  function isOffFlag(key, v) { return OFF_FLAG_KEYS.indexOf(key) !== -1 && /^\s*no\s*$/i.test(String(v == null ? '' : v)); }
  function dropOnAdd(key, v) { return isRetiredNote(key, v) || isOffFlag(key, v); }
  /* [name, value] pairs (form names) -> pairs without a retired note or an off flag, else null */
  function dropRetiredPairs(pairs) {
    var out = pairs.filter(function (p) { var k = bareKey(p[0]); return !(k !== null && dropOnAdd(k, p[1])); });
    return out.length === pairs.length ? null : out;
  }
  /* bare-key object (JSON payloads), mutated in place */
  function dropRetiredInObject(src) {
    Object.keys(src).forEach(function (k) { if (dropOnAdd(k, src[k])) delete src[k]; });
  }

  /* ---------- No Design Notes on a print-ready line (Justin 2026-10-27/28) ----------
     The file is final: a note never reaches the order, and a note must not cost the
     line its print-ready note. Print-ready = it carries a print-ready note or
     _Print Ready Size, or its upload is a print-ready file (nt-print-ready.js, where
     loaded). Shared with the cart's note editor (component-cart.js). */
  var PR_UPLOAD_KEYS = ['Upload (Vector Files Preferred)', '_Upload (Vector Files Preferred)'];
  function isPrintReadyLine(props) {
    if (!props) return false;
    var pr = window.ntPrintReady && window.ntPrintReady.isPrintReady;
    return Object.keys(props).some(function (k) {
      if (/^_?print ready size$/i.test(k)) return true;
      if (PRIMARY_REORDER_KEYS.indexOf(k) !== -1 && PRINT_READY_NOTE.test(String(props[k] || ''))) return true;
      return !!pr && PR_UPLOAD_KEYS.indexOf(k) !== -1 && pr(props[k]);
    });
  }
  window.ntIsPrintReadyLine = isPrintReadyLine;

  /* ---------- One upload property per line (Justin 2026-11-02) ----------
     A line carries its file once, as "_Upload (Vector Files Preferred)", and our 600 px
     print-ready preview is never that file. NT-2798575, NT-2803683 and NT-2819505 printed the
     preview: the My Orders one-line Reorder sent a visible "Upload (Vector Files Preferred)"
     (jQuery writes the space as "+", which the rename below missed), then the cart's upload heal
     (blanks-price-patcher.js), seeing no hidden key, added "_Upload" and "_Original Image" built
     from the line's _cartImg, our preview. The add paths now rename "+"-encoded names too; the
     sweep below puts lines already in a cart right. */
  var UPLOAD_VISIBLE = 'Upload (Vector Files Preferred)';
  var UPLOAD_HIDDEN = '_Upload (Vector Files Preferred)';
  var OUR_PREVIEW_RE = /^https?:\/\/ninjatransfers-production-art\.s3[.\w-]*\.amazonaws\.com\/art\/[^?#]*\.preview\.png(?:[?#]|$)/i;
  function isOurPreview(u) { return OUR_PREVIEW_RE.test(String(u || '')); }
  /* the print-ready file a preview was made from, when the page knows it (nt-print-ready.js) */
  function fileForPreview(u) {
    try { return (window.ntPrintReady && window.ntPrintReady.fileForPreview && window.ntPrintReady.fileForPreview(u)) || ''; } catch (e) { return ''; }
  }
  /* The properties a line should carry instead (one hidden upload key, first; no preview as the
     file), or null when it is right already. */
  function uploadFix(props) {
    if (!props) return null;
    var has = function (k) { return Object.prototype.hasOwnProperty.call(props, k); };
    if (!has(UPLOAD_VISIBLE) && !has(UPLOAD_HIDDEN)) return null;
    var v = String(props[UPLOAD_VISIBLE] || ''), h = String(props[UPLOAD_HIDDEN] || '');
    var file = (v && !isOurPreview(v)) ? v : (h && !isOurPreview(h)) ? h : (fileForPreview(v || h) || v || h);
    var origBad = isOurPreview(props['_Original Image']) && !isOurPreview(file);
    if (!has(UPLOAD_VISIBLE) && h === file && !origBad) return null;
    var out = {};
    out[UPLOAD_HIDDEN] = file;
    Object.keys(props).forEach(function (k) { if (k !== UPLOAD_VISIBLE && k !== UPLOAD_HIDDEN) out[k] = props[k]; });
    if (origBad) out['_Original Image'] = file;
    return out;
  }

  /* ---------- Reorder-ref SWEEP (post-hoc, 2026-10-09) ----------
     The interceptors above only see adds made FROM the page. A server-side
     add — a crafted GET /cart/add permalink is the proven case — lands a
     fresh note + reorder ref untouched. This sweep applies the SAME rule to
     lines already in the cart: it observes cart JSON already passing through
     fetch (zero extra requests) plus one /cart.js read on the cart page, and
     strips the reorder keys off any line carrying a non-carried note.
     Carried "of order #" notes stay exempt, exactly as everywhere else.
     It also takes the retired "previously used" notes off lines added before
     their retirement (2026-10-28), and Design Notes off a print-ready line
     (its print-ready note stays). The corrective /cart/change goes through
     the NATIVE fetch, so the sweep never re-triggers itself. */
  var sweepNativeFetch = null;
  var sweepSeen = {};
  var sweepBusy = false;
  /* What a line already in the cart must lose: null = nothing, else a test
     for the property keys to drop. */
  function sweepDrop(item) {
    var props = item && item.properties;
    if (!props) return null;
    var noted = false, carried = false, hasReorder = false, retired = false;
    Object.keys(props).forEach(function (k) {
      if (isNoteKey(k) && hasText(props[k])) noted = true;
      if (isReorderKey(k)) {
        hasReorder = true;
        if (isCarriedReorderValue(props[k])) carried = true;
      }
      if (isRetiredNote(k, props[k])) retired = true;
    });
    if (noted && isPrintReadyLine(props)) return function (k) { return isNoteKey(k) || isRetiredNote(k, props[k]); };
    if (noted && hasReorder && !carried) return isReorderKey;
    if (retired) return function (k) { return isRetiredNote(k, props[k]); };
    return null;
  }
  function sweepItems(items) {
    if (sweepBusy || !items || !items.length) return;
    var f = sweepNativeFetch || window.fetch;
    var offenders = items.filter(function (it) {
      return it && it.key && !sweepSeen[it.key] && (sweepDrop(it) || uploadFix(it.properties));
    });
    if (!offenders.length) return;
    sweepBusy = true;
    var chain = Promise.resolve();
    offenders.forEach(function (it) {
      sweepSeen[it.key] = 1;
      /* one upload property (see above), then the note rules on the result */
      var fixed = uploadFix(it.properties) || it.properties;
      var drop = sweepDrop({ properties: fixed }) || function () { return false; };
      var props = {};
      Object.keys(fixed).forEach(function (k) {
        if (!drop(k)) props[k] = fixed[k];
      });
      chain = chain.then(function () {
        return f('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props })
        }).catch(function () {});
      });
    });
    chain.then(function () { sweepBusy = false; });
  }
  function sweepFromCartJson(j) {
    if (!j) return;
    /* Broadcast every cart JSON seen at this boundary (nt-design-sync.js:
       My Designs follows what was added to cart, Justin 2026-10-13). */
    try { document.dispatchEvent(new CustomEvent('nt:cart-json', { detail: j })); } catch (e) {}
    if (j.items) sweepItems(j.items);
    else if (j.key && j.properties) sweepItems([j]); /* single-line /cart/add.js shape */
  }

  var PROPS_TO_HIDE = [
    'Upload (Vector Files Preferred)',
    'Preview',
    'Edit',
    'Remove Background',
    'Super Resolution',
    'design re-used',
    'reorder'
  ];

  /* Property whose RENAMED form (with leading "_") should appear first
     in the submitted payload so it's the top entry in the admin view.
     On gang-sheet-builder products we use a different priority key —
     see `getActivePriorityKey()` below. */
  var DEFAULT_PRIORITY_KEY = 'Upload (Vector Files Preferred)';

  /* After the priority key, properties are emitted in this order at the
     cart-add boundary. Anything not in this list falls through to the
     "rest" group and comes after the ordered ones. Shopify admin renders
     line-item properties in submission order, so this controls the
     display order on the order detail page.

     Keys are matched as `properties[<key>]` — both the renamed-hidden
     forms (e.g. `_Remove Background`) and the visible forms (e.g.
     `Remove Background`) are listed so the reorder still applies whether
     this script runs before or after `bg-removal-multi.js` does its
     `_BG Removal Service` injection. */
  var DEFAULT_DESIRED_ORDER = [
    /* Canonical admin display order (2026-08-12). The priority key
       `_Upload (Vector Files Preferred)` is pinned first separately. */
    '_Original Image',
    'Remove Background', '_Remove Background',
    'width', '_width',
    'height', '_height',
    'Design Notes',
    'Shape',
    'Border Thickness',
    'Border Color',
    'Super Resolution', '_Super Resolution',
    'design re-used', '_design re-used',
    '_reorder',
    'reorder',
    '_discount_input',
    '_discount_name',
    /* Tail group — not part of the requested sequence, kept here so their
       position stays deterministic (after the canonical block, before any
       unknown leftovers). */
    '_Size',
    '_cartImg',
    'DPI Warning',
    '_BG Removal Service',
    'AI Edit',
    'AI Created',
    '_Ready to Press'
  ];

  /* ===== Spangle (v4 template) override =====
     Justin 2026-09-04: admin order for a spangle line is Upload, Spangle
     Mockup, Original Image, width, height, Remove Background, Super
     Resolution, cartImg, Spangle Colors. `_BG Removal Service` and
     `_Spangle Sizes` are dropped from spangle lines (not needed by
     production). Scoped by the v4 spangle template body class so the legacy
     spangle page and every other product keep their current order. */
  var SPANGLE_TEMPLATE_CLASS = 'template-product-spangle-by-size-v4';
  var SPANGLE_DESIRED_ORDER = [
    '_Spangle Mockup',
    '_Original Image',
    'width', '_width',
    'height', '_height',
    'Remove Background', '_Remove Background',
    'Super Resolution', '_Super Resolution',
    '_cartImg',
    '_Spangle Colors',
    'Design Notes',
    '_discount_input',
    '_discount_name',
    'DPI Warning',
    'AI Edit',
    'AI Created',
    '_Ready to Press',
    '_Size'
  ];
  var SPANGLE_DROP_KEYS = ['_BG Removal Service', '_Spangle Sizes'];
  function isSpangleProduct() {
    var body = document.body;
    return !!(body && body.classList && body.classList.contains(SPANGLE_TEMPLATE_CLASS));
  }
  /* bare key (JSON payloads) / `properties[key]` name (form payloads) */
  function isDroppedKey(key) {
    return isSpangleProduct() && SPANGLE_DROP_KEYS.indexOf(key) !== -1;
  }
  function isDroppedName(name) {
    if (!isSpangleProduct()) return false;
    for (var i = 0; i < SPANGLE_DROP_KEYS.length; i++) {
      if (name === 'properties[' + SPANGLE_DROP_KEYS[i] + ']') return true;
    }
    return false;
  }

  /* ===== Gang-sheet-builder product overrides =====
     For build-a-gang-sheet, build-a-gang-sheet-uv, combine-gang-sheets,
     transfer-builder, builder-v2, uv-dtf-sticker-builder, and
     names-and-numbers-builder templates, the user wants this specific
     property order in the Shopify admin order detail page. All six are
     already underscore-prefixed so they're auto-hidden from cart/checkout
     by Shopify — only the order matters here. */
  var BUILDER_TEMPLATE_CLASSES = [
    'template-product-build-a-gang-sheet',
    'template-product-build-a-gang-sheet-uv',
    'template-product-combine-gang-sheets',
    'template-product-transfer-builder',
    'template-product-builder-v2',
    'template-product-uv-dtf-sticker-builder',
    'template-product-names-and-numbers-builder'
  ];
  var BUILDER_DESIRED_ORDER = [
    '_Print Ready File',
    '_Preview',
    '_Admin Edit',
    '_Edit',
    '_Has Low Resolution',
    '_Design Name'
  ];
  var BUILDER_PRIORITY_KEY = '_Print Ready File';

  /* ===== Keychain product-type override =====
     For product_type "keychains" the admin order detail should read:
     Upload, Original Image, Design Notes, width, height, Shape,
     Border Thickness, Border Color, Select Backing, Super Resolution —
     whatever is left goes last. Priority key is unchanged.

     Detection reads window.meta.product.type (Shopify's analytics meta
     blob). That blob is injected late-<head>, AFTER this script parses,
     so the check may only run lazily at request time — never capture it
     at top level. Match must be EXACT: the store also sells a merch item
     with type "ninja keychain" that must NOT match. The body-class check
     is a fallback for the current keychain template in case the meta
     blob is ever stripped. */
  var KEYCHAIN_DESIRED_ORDER = [
    '_Original Image',
    'Design Notes',
    'width', '_width',
    'height', '_height',
    'Shape',
    'Border Thickness',
    'Border Color',
    'Select Backing',
    'Super Resolution', '_Super Resolution',
    /* Tail group — "whatever is left", kept deterministic. */
    'Remove Background', '_Remove Background',
    'design re-used', '_design re-used',
    '_reorder',
    'reorder',
    '_discount_input',
    '_discount_name',
    '_Size',
    '_cartImg',
    'DPI Warning',
    '_BG Removal Service',
    'AI Edit',
    'AI Created',
    '_Ready to Press'
  ];

  /* Sticker product types (Justin 2026-08-26): Shape sits ABOVE width in the
     admin order detail. Exact-match set, lazily read like the keychain check. */
  var STICKER_TYPES = { 'stickers': 1, 'truck stickers': 1, 'bumper stickers': 1, 'large stickers': 1 };
  var STICKER_DESIRED_ORDER = [
    '_Original Image',
    'Remove Background', '_Remove Background',
    'Shape',
    'width', '_width',
    'height', '_height',
    'Design Notes',
    'Background Color',
    'Finish',
    'Border Thickness',
    'Border Color',
    'Super Resolution', '_Super Resolution',
    'design re-used', '_design re-used',
    '_reorder',
    'reorder',
    '_discount_input',
    '_discount_name',
    '_Size',
    '_cartImg',
    'DPI Warning',
    '_BG Removal Service',
    'AI Edit',
    'AI Created',
    '_Ready to Press'
  ];
  function isStickerProduct() {
    try {
      var m = window.meta;
      if (m && m.product && typeof m.product.type === 'string' &&
          STICKER_TYPES[m.product.type.toLowerCase().trim()] === 1) return true;
    } catch (e) {}
    return false;
  }

  function isKeychainProduct() {
    try {
      var m = window.meta;
      if (m && m.product && typeof m.product.type === 'string' &&
          m.product.type.toLowerCase() === 'keychains') return true;
    } catch (e) {}
    var body = document.body;
    return !!(body && body.classList &&
              body.classList.contains('template-product-custom-keychains'));
  }

  function isGangSheetBuilder() {
    var body = document.body;
    if (!body || !body.classList) return false;
    for (var i = 0; i < BUILDER_TEMPLATE_CLASSES.length; i++) {
      if (body.classList.contains(BUILDER_TEMPLATE_CLASSES[i])) return true;
    }
    return false;
  }

  /* These getters resolve the active ordering for the current page each
     time they're called (the body class is stable post-load). */
  function getActiveDesiredOrder() {
    if (isGangSheetBuilder()) return BUILDER_DESIRED_ORDER;
    if (isKeychainProduct())  return KEYCHAIN_DESIRED_ORDER;
    if (isStickerProduct())   return STICKER_DESIRED_ORDER;
    if (isSpangleProduct())   return SPANGLE_DESIRED_ORDER;
    return DEFAULT_DESIRED_ORDER;
  }
  function getActivePriorityKey() {
    return isGangSheetBuilder() ? BUILDER_PRIORITY_KEY : DEFAULT_PRIORITY_KEY;
  }

  /* PRIORITY_KEY / PRIORITY_HIDDEN_KEY / etc. need to be functions now
     so they read the current body-class context. To keep the existing
     code paths working with minimal churn, expose `PRIORITY_*` as
     getter-style helpers that callers invoke fresh. */
  function priorityKey()         { return getActivePriorityKey(); }
  function priorityHiddenKey()   { return priorityKey().charAt(0) === '_' ? priorityKey() : '_' + priorityKey(); }
  function priorityVisibleName() { return 'properties[' + priorityKey() + ']'; }
  function priorityHiddenName()  { return 'properties[' + priorityHiddenKey() + ']'; }

  function desiredIndex(name) {
    var order = getActiveDesiredOrder();
    for (var i = 0; i < order.length; i++) {
      if (name === 'properties[' + order[i] + ']') return i;
    }
    return -1;
  }

  /* Build name pairs for FormData / URLSearchParams / URL-encoded payloads */
  var NAME_PAIRS = PROPS_TO_HIDE.map(function (key) {
    return {
      visibleKey:  key,
      hiddenKey:   '_' + key,
      visibleName: 'properties[' + key + ']',
      hiddenName:  'properties[_' + key + ']'
    };
  });

  /* Return {hiddenName, value} if `name` is a visible property we rename,
     or {hiddenName: name, value} if it's already hidden, else null. */
  function renameLookup(name) {
    for (var i = 0; i < NAME_PAIRS.length; i++) {
      if (name === NAME_PAIRS[i].visibleName) {
        return { name: NAME_PAIRS[i].hiddenName, wasRenamed: true };
      }
      if (name === NAME_PAIRS[i].hiddenName) {
        return { name: name, wasRenamed: false };
      }
    }
    return null;
  }

  /* ---------- body rewriting ---------- */
  function rewriteBody(body) {
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return rewriteFormData(body);
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return rewriteURLSearchParams(body);
      }
      if (typeof body === 'string') {
        var trimmed = body.replace(/^\s+/, '');
        if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
          try {
            var json = JSON.parse(body);
            renameAndReorderInJSON(json);
            return JSON.stringify(json);
          } catch (e) { /* not JSON */ }
        }
        return rewriteURLEncodedString(body);
      }
      /* a plain object (jQuery `data` before it serializes it): the same rules as a JSON body */
      if (body && typeof body === 'object' && (body.properties || Array.isArray(body.items))) {
        renameAndReorderInJSON(body);
        return body;
      }
    } catch (e) { /* never block */ }
    return body;
  }

  /* Split `rest` into ordered (those whose name is in DESIRED_ORDER, sorted
     by their DESIRED_ORDER index) + leftover (everything else, original
     order preserved). Returns concatenated [orderedSorted, leftover]. */
  function reorderRest(rest) {
    var ordered = [];
    var leftover = [];
    rest.forEach(function (p) {
      var idx = desiredIndex(p[0]);
      if (idx >= 0) ordered.push({ idx: idx, pair: p });
      else leftover.push(p);
    });
    ordered.sort(function (a, b) { return a.idx - b.idx; });
    return ordered.map(function (o) { return o.pair; }).concat(leftover);
  }

  /* Keep the size pair stacked: move the height entry directly after the
     width entry (whichever visible/hidden form each uses). Runs on EVERY
     cart-add payload — unlike the DESIRED_ORDER reorder, this is not gated
     on the priority upload property being present, because payloads built
     without an upload (or rebuilt by upsell / edit flows) were reaching the
     admin with width and height separated by other properties. No-op when
     the pair is already adjacent or either half is missing. Operates on an
     array of [name, value] pairs where name is `properties[...]`. */
  var WIDTH_NAMES  = ['properties[width]', 'properties[_width]'];
  var HEIGHT_NAMES = ['properties[height]', 'properties[_height]'];
  function pairWidthHeight(pairs, getName) {
    var name = getName || function (p) { return p[0]; };
    var wIdx = -1, hIdx = -1;
    for (var i = 0; i < pairs.length; i++) {
      if (wIdx === -1 && WIDTH_NAMES.indexOf(name(pairs[i])) !== -1) wIdx = i;
      if (hIdx === -1 && HEIGHT_NAMES.indexOf(name(pairs[i])) !== -1) hIdx = i;
    }
    if (wIdx === -1 || hIdx === -1 || hIdx === wIdx + 1) return false;
    var height = pairs.splice(hIdx, 1)[0];
    if (hIdx < wIdx) wIdx--;
    pairs.splice(wIdx + 1, 0, height);
    return true;
  }

  function rewriteFormData(body) {
    var PRIORITY_VISIBLE_NAME = priorityVisibleName();
    var PRIORITY_HIDDEN_NAME  = priorityHiddenName();
    var priority = null;
    var rest = [];
    var entries = [];
    body.forEach(function (value, name) { entries.push([name, value]); });
    /* Design Note wipes reorder references (see header). Done on the entry
       list so it holds on the no-priority path too, which otherwise returns
       the body untouched. */
    var strippedFD = stripReorderPairs(entries);
    if (strippedFD) {
      entries = strippedFD;
      body = new FormData();
      entries.forEach(function (p) { body.append(p[0], p[1]); });
    }
    var oneFD = dropRetiredPairs(entries);
    if (oneFD) {
      entries = oneFD;
      body = new FormData();
      entries.forEach(function (p) { body.append(p[0], p[1]); });
    }
    entries.forEach(function (pair) {
      var name  = pair[0];
      var value = pair[1];
      if (isDroppedName(name)) return;
      if (name === PRIORITY_VISIBLE_NAME || name === PRIORITY_HIDDEN_NAME) {
        priority = [PRIORITY_HIDDEN_NAME, value];
        return;
      }
      var renamed = renameLookup(name);
      if (renamed) {
        rest.push([renamed.name, value]);
      } else {
        rest.push([name, value]);
      }
    });
    if (!priority) {
      /* No priority upload property: leave names untouched (the original
         behavior) but still stack height under width. Rebuild only when
         the pair actually moved. */
      if (!pairWidthHeight(entries)) return body;
      var outNP = new FormData();
      entries.forEach(function (p) { outNP.append(p[0], p[1]); });
      return outNP;
    }
    rest = reorderRest(rest);
    pairWidthHeight(rest);
    var out = new FormData();
    out.append(priority[0], priority[1]);
    rest.forEach(function (p) { out.append(p[0], p[1]); });
    return out;
  }

  function rewriteURLSearchParams(body) {
    var PRIORITY_VISIBLE_NAME = priorityVisibleName();
    var PRIORITY_HIDDEN_NAME  = priorityHiddenName();
    var priority = null;
    var rest = [];
    var entries = [];
    body.forEach(function (value, name) { entries.push([name, value]); });
    /* Design Note wipes reorder references (see header). */
    var strippedSP = stripReorderPairs(entries);
    if (strippedSP) {
      entries = strippedSP;
      body = new URLSearchParams();
      entries.forEach(function (p) { body.append(p[0], p[1]); });
    }
    var oneSP = dropRetiredPairs(entries);
    if (oneSP) {
      entries = oneSP;
      body = new URLSearchParams();
      entries.forEach(function (p) { body.append(p[0], p[1]); });
    }
    entries.forEach(function (pair) {
      var name = pair[0], value = pair[1];
      if (name === PRIORITY_VISIBLE_NAME || name === PRIORITY_HIDDEN_NAME) {
        priority = [PRIORITY_HIDDEN_NAME, value];
        return;
      }
      var renamed = renameLookup(name);
      if (renamed) {
        rest.push([renamed.name, value]);
      } else {
        rest.push([name, value]);
      }
    });
    if (!priority) {
      /* Same no-priority contract as rewriteFormData: arrangement only. */
      if (!pairWidthHeight(entries)) return body;
      var outNP = new URLSearchParams();
      entries.forEach(function (p) { outNP.append(p[0], p[1]); });
      return outNP;
    }
    rest = reorderRest(rest);
    pairWidthHeight(rest);
    var out = new URLSearchParams();
    out.append(priority[0], priority[1]);
    rest.forEach(function (p) { out.append(p[0], p[1]); });
    return out;
  }

  function rewriteURLEncodedString(body) {
    var PRIORITY_VISIBLE_NAME = priorityVisibleName();
    var PRIORITY_HIDDEN_NAME  = priorityHiddenName();
    var encPriorityVisible = encodeURIComponent(PRIORITY_VISIBLE_NAME);
    var encPriorityHidden  = encodeURIComponent(PRIORITY_HIDDEN_NAME);
    /* Names in one spelling first: jQuery (an object `data`) and form serializers write a space as
       "+", and URLSearchParams escapes "(" — the exact-name matches below then missed the upload
       (the My Orders one-line Reorder, see "One upload property per line"). Values stay as sent. */
    var parts = body.split('&').map(function (part) {
      var eq = part.indexOf('=');
      var name = eq === -1 ? part : part.slice(0, eq);
      var std;
      try { std = encodeURIComponent(decodeURIComponent(name.replace(/\+/g, ' '))); } catch (e) { return part; }
      return eq === -1 ? std : std + part.slice(eq);
    });
    /* Design Note wipes reorder references (see header). Decode each part to
       [name, value], apply the rule, keep the ORIGINAL encoded parts. */
    (function () {
      function dec(s) { try { return decodeURIComponent(String(s).replace(/\+/g, ' ')); } catch (_e) { return String(s); } }
      var decoded = parts.map(function (part) {
        var eq = part.indexOf('=');
        return eq === -1 ? [dec(part), ''] : [dec(part.slice(0, eq)), dec(part.slice(eq + 1))];
      });
      if (!stripReorderPairs(decoded)) return;
      parts = parts.filter(function (part) {
        var eq = part.indexOf('=');
        var k = bareKey(dec(eq === -1 ? part : part.slice(0, eq)));
        return !(k !== null && isReorderKey(k));
      });
      body = parts.join('&');
    })();
    /* Retired "previously used" notes (see header), on the original encoded parts. */
    (function () {
      function dec(s) { try { return decodeURIComponent(String(s).replace(/\+/g, ' ')); } catch (_e) { return String(s); } }
      var decoded = parts.map(function (part) {
        var eq = part.indexOf('=');
        return eq === -1 ? [dec(part), ''] : [dec(part.slice(0, eq)), dec(part.slice(eq + 1))];
      });
      if (!dropRetiredPairs(decoded)) return;
      parts = parts.filter(function (_part, i) {
        var k = bareKey(decoded[i][0]);
        return !(k !== null && dropOnAdd(k, decoded[i][1]));
      });
      body = parts.join('&');
    })();
    var priorityPart = null;
    var rest = [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      var eq = part.indexOf('=');
      if (eq === -1) { rest.push(part); continue; }
      var encName = part.slice(0, eq);
      if (encName === encPriorityVisible || encName === encPriorityHidden) {
        priorityPart = encPriorityHidden + part.slice(eq);
        continue;
      }
      var rewritten = part;
      for (var j = 0; j < NAME_PAIRS.length; j++) {
        var encV = encodeURIComponent(NAME_PAIRS[j].visibleName);
        if (encName === encV) {
          rewritten = encodeURIComponent(NAME_PAIRS[j].hiddenName) + part.slice(eq);
          break;
        }
      }
      rest.push(rewritten);
    }
    function decodedName(part) {
      var eq = part.indexOf('=');
      var encName = eq === -1 ? part : part.slice(0, eq);
      /* `+` means space in application/x-www-form-urlencoded */
      try { return decodeURIComponent(encName.replace(/\+/g, ' ')); }
      catch (_e) { return encName; }
    }
    if (!priorityPart) {
      /* Same no-priority contract as rewriteFormData: arrangement only.
         `parts` here are the ORIGINAL un-renamed pieces. */
      if (!pairWidthHeight(parts, decodedName)) return body;
      return parts.join('&');
    }
    /* Reorder `rest` so DESIRED_ORDER members lead. Each `part` is
       `encName=encVal`; we decode the name (which is `properties[...]`),
       look up its index, and sort with the same logic used for
       FormData / URLSearchParams. */
    var ordered = [];
    var leftover = [];
    rest.forEach(function (part) {
      var idx = desiredIndex(decodedName(part));
      if (idx >= 0) ordered.push({ idx: idx, part: part });
      else leftover.push(part);
    });
    ordered.sort(function (a, b) { return a.idx - b.idx; });
    var reordered = ordered.map(function (o) { return o.part; }).concat(leftover);
    pairWidthHeight(reordered, decodedName);
    return [priorityPart].concat(reordered).join('&');
  }

  /* Object-key flavor of pairWidthHeight for JSON cart-add payloads, where
     properties is a plain object keyed by the bare property name. Returns a
     rebuilt object with the height key directly after the width key, or the
     original object when nothing needs to move. */
  function pairWidthHeightInObject(src) {
    var keys = Object.keys(src);
    var wIdx = -1, hIdx = -1;
    for (var i = 0; i < keys.length; i++) {
      if (wIdx === -1 && (keys[i] === 'width' || keys[i] === '_width')) wIdx = i;
      if (hIdx === -1 && (keys[i] === 'height' || keys[i] === '_height')) hIdx = i;
    }
    if (wIdx === -1 || hIdx === -1 || hIdx === wIdx + 1) return src;
    var hKey = keys.splice(hIdx, 1)[0];
    if (hIdx < wIdx) wIdx--;
    keys.splice(wIdx + 1, 0, hKey);
    var out = {};
    keys.forEach(function (k) { out[k] = src[k]; });
    return out;
  }

  function renameAndReorderInJSON(obj) {
    if (!obj || typeof obj !== 'object') return;
    var PRIORITY_KEY        = priorityKey();
    var PRIORITY_HIDDEN_KEY = priorityHiddenKey();
    var DESIRED_ORDER       = getActiveDesiredOrder();
    if (obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties)) {
      var src = obj.properties;
      /* A print-ready file takes no Design Notes (nt-print-ready.js, Justin 2026-10-27): dropped
         first, so the rule below keeps its reorder note. */
      try { if (window.ntPrintReady && window.ntPrintReady.dropNotes) window.ntPrintReady.dropNotes(src); } catch (e) {}
      /* Design Note wipes reorder references (see header). */
      stripReorderInObject(src);
      /* Retired "previously used" notes (see header). */
      dropRetiredInObject(src);
      var priorityValue;
      var hasPriority = false;
      if (Object.prototype.hasOwnProperty.call(src, PRIORITY_KEY)) {
        priorityValue = src[PRIORITY_KEY];
        hasPriority = true;
        delete src[PRIORITY_KEY];
      } else if (Object.prototype.hasOwnProperty.call(src, PRIORITY_HIDDEN_KEY)) {
        priorityValue = src[PRIORITY_HIDDEN_KEY];
        hasPriority = true;
        delete src[PRIORITY_HIDDEN_KEY];
      }
      /* never our preview as the file (see "One upload property per line") */
      if (hasPriority && PRIORITY_HIDDEN_KEY === UPLOAD_HIDDEN && isOurPreview(priorityValue)) {
        var otherUpload = src[PRIORITY_HIDDEN_KEY];
        priorityValue = (otherUpload && !isOurPreview(otherUpload)) ? otherUpload : (fileForPreview(priorityValue) || priorityValue);
      }
      if (hasPriority && isOurPreview(src['_Original Image']) && !isOurPreview(priorityValue)) src['_Original Image'] = priorityValue;
      /* Rename remaining hidden-targets in place (preserves their relative order) */
      NAME_PAIRS.forEach(function (p) {
        if (p.visibleKey === PRIORITY_KEY) return;
        if (Object.prototype.hasOwnProperty.call(src, p.visibleKey)) {
          src[p.hiddenKey] = src[p.visibleKey];
          delete src[p.visibleKey];
        }
      });
      Object.keys(src).forEach(function (k) { if (isDroppedKey(k)) delete src[k]; });
      if (hasPriority) {
        var rebuilt = {};
        rebuilt[PRIORITY_HIDDEN_KEY] = priorityValue;
        /* Add DESIRED_ORDER members first (sorted), then leftovers in their
           original iteration order. `DESIRED_ORDER` strings here are the
           bare property key (no `properties[…]` wrapper) — JSON cart-add
           payloads store properties as an object whose keys are the bare
           property names. */
        DESIRED_ORDER.forEach(function (key) {
          if (Object.prototype.hasOwnProperty.call(src, key)) {
            rebuilt[key] = src[key];
          }
        });
        for (var k in src) {
          if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
          if (k === PRIORITY_HIDDEN_KEY) continue;
          if (DESIRED_ORDER.indexOf(k) !== -1) continue;
          rebuilt[k] = src[k];
        }
        obj.properties = rebuilt;
      }
      /* Always keep the size pair stacked, priority property or not. */
      obj.properties = pairWidthHeightInObject(obj.properties);
    }
    if (Array.isArray(obj.items)) {
      obj.items.forEach(renameAndReorderInJSON);
    }
  }

  /* ---------- 1. Native form submits ---------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action) return;
    if (form.action.indexOf('/cart/add') === -1) return;
    var PRIORITY_KEY = priorityKey();
    /* Design Note wipes reorder references (see header): with a non-empty
       note field in the form, the reorder fields are disabled so they never
       serialize. */
    var noted = false, carried = false;
    NOTE_KEYS.forEach(function (k) {
      var ins = form.querySelectorAll('[name="properties[' + k + ']"]');
      Array.prototype.forEach.call(ins, function (el) { if (!el.disabled && hasText(el.value)) noted = true; });
    });
    REORDER_KEYS.forEach(function (k) {
      var ins = form.querySelectorAll('[name="properties[' + k + ']"]');
      Array.prototype.forEach.call(ins, function (el) { if (!el.disabled && isCarriedReorderValue(el.value)) carried = true; });
    });
    if (noted && !carried) {
      REORDER_KEYS.forEach(function (k) {
        var ins = form.querySelectorAll('[name="properties[' + k + ']"]');
        Array.prototype.forEach.call(ins, function (el) { el.disabled = true; });
      });
    }
    /* Retired "previously used" notes (see header): never serialized. */
    REUSED_KEYS.concat(PRIMARY_REORDER_KEYS).forEach(function (k) {
      var ins = form.querySelectorAll('[name="properties[' + k + ']"]');
      Array.prototype.forEach.call(ins, function (el) { if (isRetiredNote(k, el.value)) el.disabled = true; });
    });
    /* …and Remove Background / Super Resolution only when Yes */
    OFF_FLAG_KEYS.forEach(function (k) {
      var ins = form.querySelectorAll('[name="properties[' + k + ']"]');
      Array.prototype.forEach.call(ins, function (el) { if (isOffFlag(k, el.value)) el.disabled = true; });
    });
    NAME_PAIRS.forEach(function (p) {
      var inputs = form.querySelectorAll('input[name="' + p.visibleName + '"]');
      Array.prototype.forEach.call(inputs, function (input) {
        if (input.disabled || !input.value) return;
        input.disabled = true;
        var hidden = document.createElement('input');
        hidden.type  = 'hidden';
        hidden.name  = p.hiddenName;
        hidden.value = input.value;
        /* For the priority property, insert at the START of the form so its
           field-order beats anything appended later. */
        if (p.visibleKey === PRIORITY_KEY && form.firstChild) {
          form.insertBefore(hidden, form.firstChild);
        } else {
          form.appendChild(hidden);
        }
      });
    });
  }, true);

  /* ---------- 2. fetch ---------- */
  function wrapFetch() {
    if (!window.fetch || window.fetch.__hupWrapped) return;
    var origFetch = window.fetch;
    var wrapped = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1 && init && init.body) {
          init = Object.assign({}, init, { body: rewriteBody(init.body) });
        }
      } catch (e) {}
      var res = origFetch.call(this, input, init);
      try {
        var u = (typeof input === 'string') ? input : (input && input.url) || '';
        var uPath = u.split('?')[0];
        if (/\/cart\.js$|\/cart\/(add|change|update)(\.js)?$/.test(uPath)) {
          res.then(function (r) {
            try {
              r.clone().json().then(sweepFromCartJson).catch(function () {});
            } catch (e) {}
          }).catch(function () {});
        }
      } catch (e) {}
      return res;
    };
    wrapped.__hupWrapped = true;
    sweepNativeFetch = origFetch.bind(window);
    window.fetch = wrapped;
  }

  /* ---------- 3. XMLHttpRequest ---------- */
  function wrapXHR() {
    if (!window.XMLHttpRequest) return;
    if (XMLHttpRequest.prototype.send.__hupWrapped) return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    var wrappedOpen = function (method, url) {
      this.__hupUrl = url;
      return origOpen.apply(this, arguments);
    };
    wrappedOpen.__hupWrapped = true;
    XMLHttpRequest.prototype.open = wrappedOpen;
    var wrappedSend = function (body) {
      try {
        if (this.__hupUrl && this.__hupUrl.indexOf('/cart/add') !== -1 && body) {
          body = rewriteBody(body);
        }
        /* jQuery / XHR adds: hand the cart JSON to the same broadcast */
        if (this.__hupUrl && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(String(this.__hupUrl)) && !this.__hupObs) {
          this.__hupObs = true;
          this.addEventListener('load', function () {
            try {
              if (this.status >= 200 && this.status < 300) {
                var t = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
                if (t) document.dispatchEvent(new CustomEvent('nt:cart-json', { detail: t }));
              }
            } catch (e) {}
          });
        }
      } catch (e) {}
      return origSend.call(this, body);
    };
    wrappedSend.__hupWrapped = true;
    XMLHttpRequest.prototype.send = wrappedSend;
  }

  /* ---------- 4. jQuery $.ajax ---------- */
  function wrapJQuery() {
    if (!window.jQuery || !window.jQuery.ajax || window.jQuery.ajax.__hupWrapped) return;
    var $ = window.jQuery;
    var origAjax = $.ajax;
    var wrappedAjax = function (urlOrOpts, opts) {
      try {
        var settings = (typeof urlOrOpts === 'object') ? urlOrOpts : (opts || {});
        var url = (typeof urlOrOpts === 'string') ? urlOrOpts : settings.url;
        if (url && url.indexOf('/cart/add') !== -1 && settings.data) {
          settings.data = rewriteBody(settings.data);
        }
      } catch (e) {}
      return origAjax.apply(this, arguments);
    };
    wrappedAjax.__hupWrapped = true;
    $.ajax = wrappedAjax;
  }

  function installAll() {
    wrapFetch();
    wrapXHR();
    wrapJQuery();
  }

  installAll();
  function sweepCartPageOnce() {
    try {
      if (!document.body || !document.body.classList.contains('template-cart')) return;
      var f = sweepNativeFetch || window.fetch;
      f('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(sweepFromCartJson)
        .catch(function () {});
    } catch (e) {}
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweepCartPageOnce);
  } else {
    sweepCartPageOnce();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installAll);
  }
  window.addEventListener('load', installAll);
  setTimeout(installAll, 250);
  setTimeout(installAll, 1000);
  setTimeout(installAll, 3000);
  setTimeout(installAll, 8000);
})();
